import React, { useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Box, Typography } from "@mui/material";
import { AccessTimeOutlined } from "@mui/icons-material";
import { translate } from "../../../../translate/translate";
import { BaseService } from "../../../../services/base-service";
import {
  Analysis,
  AnalysisResultExtractType,
  ExtractData,
  ExtractItem,
  StockAndInTransitExtractData,
  StockExtractItem,
} from "../../../../models/general/analysis";
import { URLS } from "../../../../services/app-urls";
import { ErrorComponent } from "../../../../components/error/error.component";
import {
  CardHeader,
  CellLabel,
  Chip,
  DialogBar,
  ExtractCard,
  Foot,
  ParentRef,
  Qty,
  Row,
  Rows,
  SummaryRow,
  T,
  formatValue,
} from "./extract-shared";

/** Splits "CODE (PR-1)" into the card name and the badge. */
const splitBadge = (name: string): { label: string; badge: string | null } => {
  const match = name.match(/^(.*?)\s*\((PR-\d+|ALT-\d+)\)\s*$/);
  if (match) return { label: match[1], badge: match[2] };
  return { label: name, badge: null };
};

const DialogResultStock = (props: any) => {
  const { open, onClose, title, analysisResultIds } = props;

  const service = new BaseService<Analysis>(URLS.ANALYSIS);
  const [data, setData] = React.useState<StockAndInTransitExtractData>();
  const [isProcessingError, setIsProcessingError] = React.useState<boolean>(false);

  const handleCloseDialog = () => {
    onClose();
  };

  useEffect(() => {
    setData(undefined);
    getStockInfo();
  }, [analysisResultIds]);

  const getStockInfo = () => {
    if (analysisResultIds.length === 0) return;
    setIsProcessingError(false);
    service.clearParameters();
    service.addParameter("analysis_result_ids", analysisResultIds);
    service.addParameter("extract_type", AnalysisResultExtractType.STOCK);
    service
      .getAllFromListRoute("extract")
      .then((response: any) => {
        setData(response?.data);
      })
      .catch((error: any) => {
        if (error?.response?.status === 409 || error?.response?.status === 404) {
          setIsProcessingError(true);
        } else {
          ErrorComponent(error);
        }
      });
  };

  const nonZero = (group: ExtractData<StockExtractItem>): StockExtractItem[] =>
    group?.group?.items?.filter((item) => item.quantity && item.quantity !== 0) || [];

  /**
   * Renders a group as two named rows — set aside for use vs. leftover — and surfaces the
   * substitution as card context (rail and chip).
   */
  const renderStockGroup = (group: ExtractData<StockExtractItem>, defaultRail: string) => {
    const items = nonZero(group);
    if (items.length === 0) return null;

    const consumed = items.filter((item) => item.source !== "leftover");
    const leftoverTotal = items
      .filter((item) => item.source === "leftover")
      .reduce((sum, item) => sum + item.quantity, 0);

    const substitutePartNumber = items.find((item) => item.used_substitute)?.substitute_part_number;
    const { label, badge } = splitBadge(group.name);
    const railColor = substitutePartNumber ? T.sub : defaultRail;

    return (
      <ExtractCard key={group.name}>
        <CardHeader
          railColor={railColor}
          name={label}
          kind={
            substitutePartNumber
              ? translate.t("substituted_by").replace("{pn}", substitutePartNumber)
              : undefined
          }
          right={
            substitutePartNumber ? (
              <Chip variant="sub">SUB</Chip>
            ) : badge ? (
              <Chip variant={badge.startsWith("ALT") ? "alt" : "pr"}>{badge}</Chip>
            ) : undefined
          }
        />

        <Rows>
          {consumed.map((item, index) => (
            <Row
              key={`${item.code}-${index}`}
              tint="used"
              left={
                <>
                  <CellLabel>{translate.t("stock_set_aside_for_use")}</CellLabel>
                  {/* Which BOM/final product this stock was allocated to (`code` from the response). */}
                  {item.code && <ParentRef code={item.code} arrow="→" />}
                </>
              }
              qty={<Qty tone="used">{formatValue(item.quantity)}</Qty>}
            />
          ))}
          {leftoverTotal > 0 && (
            <Row
              tint="left"
              left={<CellLabel>{translate.t("stock_leftover")}</CellLabel>}
              qty={<Qty tone="left">{formatValue(leftoverTotal)}</Qty>}
            />
          )}
        </Rows>

        <SummaryRow label={translate.t("subtotal_label")} value={formatValue(group?.group?.total)} />
      </ExtractCard>
    );
  };

  const renderLent = (group: ExtractData) => {
    const items = group?.group?.items?.filter((item: ExtractItem) => item.quantity && item.quantity !== 0) || [];
    if (items.length === 0) return null;

    return (
      <ExtractCard key={group.name}>
        <CardHeader
          railColor={T.lent}
          name={translate.t("material_in_use_by_another_pn")}
          kind={translate.t("lent_consumed_as_alternative")}
        />
        <Rows>
          {items.map((item: ExtractItem) => (
            // The destination comes from the response (`lent[].group.items[].code`).
            <Row
              key={item.code}
              tint="lent"
              left={
                <>
                  <CellLabel mono>{group.name}</CellLabel>
                  <Typography component="span" sx={{ fontSize: "11px", color: T.inkFaint }}>
                    {translate.t("lent_row_label")}
                  </Typography>
                  <Box component="b" sx={{ fontFamily: T.mono, fontSize: "11px", fontWeight: 600, color: T.inkSoft }}>
                    {item.code}
                  </Box>
                </>
              }
              qty={<Qty tone="lent">{formatValue(item.quantity)}</Qty>}
            />
          ))}
        </Rows>
        <SummaryRow label={translate.t("lent_out_of_total")} value={formatValue(group?.group?.total)} tone="lent" />
      </ExtractCard>
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogBar label={translate.t("in_stock")} pn={title} onClose={handleCloseDialog} />

      <DialogContent sx={{ maxHeight: "450px", overflowY: "auto", padding: 0, background: T.surface }}>
        {isProcessingError ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              gap: 2,
              minHeight: "300px",
              width: 380,
            }}
          >
            <AccessTimeOutlined sx={{ fontSize: 64, color: "#CFD8DC" }} />
            <Typography sx={{ color: T.inkSoft, fontWeight: 600, textAlign: "center", fontSize: "16px", maxWidth: "340px" }}>
              {translate.t("cell_data_processing")}
            </Typography>
          </Box>
        ) : (
          <Box>
            {data?.primary?.map((group) => renderStockGroup(group, T.used))}
            {data?.alternatives?.map((group) => renderStockGroup(group, T.left))}
            {data?.lent?.map((group) => renderLent(group))}
          </Box>
        )}
      </DialogContent>

      {!isProcessingError && (
        <Foot label={translate.t("total_in_stock")} value={`${formatValue(data?.total)} ${translate.t("unit")}`} />
      )}
    </Dialog>
  );
};

export default DialogResultStock;
